import { motion } from "framer-motion";
import { Check, X, Minus } from "lucide-react";
import { Eyebrow, FadeUp, MaskLines, EASE } from "@/components/site/Reveal";

const COLS = ["Chople's Desk", "ChatGPT", "Claude", "Other research tools"];
const Y = "y", N = "n", P = "p";
const ROWS = [
  ["Answers only from pre-approved, credible sources", P, P, P],
  ["Every claim traced to the original document", P, P, P],
  ["Detects your question and picks the format: chart, table or timeline", N, N, N],
  ["Interactive charts: switch line, bar & pie, copy & export data", Y, Y, P],
  ["Flags anomalies & conflicting values across sources", P, P, N],
  ["Direct access to 2 million+ live official government datasets", P, P, P],
  ["Connects events, people & policies into one narrative", N, N, N],
  ["Searches your newsroom's own internal archive", N, N, P],
  ["One-click notes with citations attached", N, N, N],
  ["Trends that link live news feeds to related coverage", N, N, P],
  ["Built for newsroom deadlines & editorial workflows", N, N, N],
];

const Mark = ({ v, hero }) => {
  if (hero) return <span className="inline-flex h-7 w-7 items-center justify-center bg-signal text-white"><Check className="h-4 w-4" strokeWidth={2.5} /></span>;
  if (v === Y) return <span className="inline-flex h-7 w-7 items-center justify-center bg-signal text-white"><Check className="h-4 w-4" strokeWidth={2.5} /></span>;
  if (v === P) return <span className="inline-flex items-center gap-1.5 font-mono text-[10.5px] uppercase tracking-[0.1em] text-[#8A8A80]"><Minus className="h-4 w-4" /> Limited</span>;
  return <span className="inline-flex h-7 w-7 items-center justify-center border border-rule text-[#A8A89E]"><X className="h-3.5 w-3.5" /></span>;
};

export const CompareTable = () => (
  <section className="scroll-mt-16 border-t border-rule bg-paper pb-12 pt-6 sm:pb-20 lg:pb-32 lg:pt-8" data-testid="compare-table-section">
    <div className="mx-auto max-w-[1440px] px-5 lg:px-10">
      <div className="grid gap-6 sm:gap-8 lg:grid-cols-12">
        <div className="lg:col-span-8">
          <Eyebrow>Chople vs other tools</Eyebrow>
          <h2 className="mt-4 sm:mt-6 font-display text-4xl sm:text-6xl font-medium leading-[0.95] tracking-[-0.03em] lg:text-[5rem]">
            <MaskLines inView lines={["How is Chople different", <em key="e" className="italic text-signal">from other tools?</em>]} />
          </h2>
        </div>
        <FadeUp delay={0.1} className="flex items-end lg:col-span-4">
          <p className="max-w-sm text-[14.5px] sm:text-[15px] leading-relaxed text-[#3A3A3A]">General-purpose assistants are brilliant writers. Newsrooms need something else: verified sources, the right format, and a trail back to the document.</p>
        </FadeUp>
      </div>

      <div className="mt-6 sm:mt-8 flex items-center justify-between font-mono text-[11px] text-[#6B6B63] lg:hidden">
        <span className="flex items-center gap-1.5"><span className="inline-block animate-pulse text-signal font-bold">←</span> Swipe horizontally to compare all tools <span className="inline-block animate-pulse text-signal font-bold">→</span></span>
        <span className="text-[#8A8A80]">11 capabilities</span>
      </div>

      <FadeUp className="mt-3 sm:mt-10 overflow-x-auto pb-4">
        <table className="w-full min-w-[620px] sm:min-w-[820px] border-collapse" data-testid="compare-table">
          <thead>
            <tr className="border-y-2 border-ink">
              <th className="sticky left-0 z-20 w-[165px] min-w-[165px] sm:w-[38%] sm:min-w-[280px] border-r border-rule bg-paper py-3.5 pr-3 pl-2 text-left font-mono text-[10.5px] font-normal uppercase tracking-[0.16em] text-[#6B6B63] shadow-[2px_0_5px_-2px_rgba(0,0,0,0.08)]">Capability</th>
              {COLS.map((c, i) => (
                <th key={c} className={`min-w-[110px] sm:min-w-[130px] px-3 sm:px-4 py-3.5 sm:py-4 text-left ${i === 0 ? "bg-ink text-paper" : ""}`}>
                  <span className={`font-display text-[18px] sm:text-[22px] font-medium tracking-tight ${i === 0 ? "" : "text-[#3A3A3A]"}`}>{c}</span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {ROWS.map(([label, ...vals], r) => (
              <motion.tr key={label} initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: r * 0.04, duration: 0.6, ease: EASE }} className="group border-b border-rule transition-colors hover:bg-paper-2" data-testid={`compare-row-${r}`}>
                <td className="sticky left-0 z-10 w-[165px] min-w-[165px] sm:w-[38%] sm:min-w-[280px] border-r border-rule bg-paper group-hover:bg-paper-2 py-3.5 pr-3 pl-2 text-[13px] sm:text-[15px] leading-snug shadow-[2px_0_5px_-2px_rgba(0,0,0,0.08)]">
                  <span className="mr-2 font-mono text-[10px] text-[#9A9A90]">{String(r + 1).padStart(2, "0")}</span>
                  {label}
                </td>
                <td className="bg-ink/[0.03] px-3 sm:px-4 py-3.5 sm:py-4"><Mark hero /></td>
                {vals.map((v, i) => <td key={i} className="px-3 sm:px-4 py-3.5 sm:py-4"><Mark v={v} /></td>)}
              </motion.tr>
            ))}
            <tr className="border-b-2 border-ink">
              <td className="sticky left-0 z-10 w-[165px] min-w-[165px] sm:w-[38%] sm:min-w-[280px] border-r border-rule bg-paper py-4 sm:py-5 pr-3 pl-2 font-mono text-[10.5px] uppercase tracking-[0.16em] text-[#6B6B63] shadow-[2px_0_5px_-2px_rgba(0,0,0,0.08)]">Score</td>
              <td className="bg-ink px-3 sm:px-4 py-4 sm:py-5 font-display text-2xl sm:text-3xl text-paper" data-testid="compare-score-chople">11 / 11</td>
              {[1, 2, 3].map((c) => (
                <td key={c} className="px-3 sm:px-4 py-4 sm:py-5 font-display text-2xl sm:text-3xl text-[#9A9A90]">{ROWS.filter((row) => row[c] === Y || row[c] === P).length} / 11</td>
              ))}
            </tr>
          </tbody>
        </table>
        <p className="mt-4 font-mono text-[10px] sm:text-[10.5px] text-[#8A8A80]">“Limited” = possible with plug-ins, manual setup or partial coverage. Based on standard, publicly available versions of each product.</p>
      </FadeUp>
    </div>
  </section>
);
